import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { HorizontalLine } from "@faire/web--source/slate/Layout";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { FaireMarketTheme } from "@faire/web--source/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { EventsThemeLogo } from "@faire/web--source/slate/Theme/EventsThemeLogo";
import { EventsThemeShape } from "@faire/web--source/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import marketsAllSpecials from "@faire/web-api--source/routes/markets/event_type/all-specials";
import React from "react";
import styled from "styled-components";
export var HomeFaireMarketHero = function(param) {
    var layoutElement = param.layoutElement, onClick = param.onClick;
    var _useMarketStore = useMarketStore([
        "isInsiderDay",
        "currentEventType"
    ]), isInsiderDay = _useMarketStore.isInsiderDay, currentEventType = _useMarketStore.currentEventType;
    var preamble = /*#__PURE__*/ _jsx(Typography, {
        "data-test-id": "market-hero-section-preamble",
        variant: "subheadingSansMedium",
        color: layoutElement.tagline_text_color,
        children: layoutElement.tagline_text
    });
    var preambleIcon = isInsiderDay ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(StyledInsider, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "1x"
            })
        ]
    }) : null;
    var title = /*#__PURE__*/ _jsx(Typography, {
        "data-test-id": "market-hero-section-title",
        variant: {
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayLSerifRegular"
        },
        color: layoutElement.main_text_color,
        children: layoutElement.main_text
    });
    var subtitle = layoutElement.description_text ? /*#__PURE__*/ _jsxs(Column, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                "data-test-id": "market-hero-section-subtitle",
                variant: {
                    mobileAndAbove: "paragraphSansRegular",
                    tabletAndAbove: "subheadingSansRegular"
                },
                color: layoutElement.description_text_color,
                children: layoutElement.description_text
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "3x"
            })
        ]
    }) : null;
    var ctaButton = /*#__PURE__*/ _jsx(ThemeProvider, {
        theme: FaireMarketTheme,
        children: /*#__PURE__*/ _jsx(StyledButton, {
            "data-test-id": "market-hero-section-cta",
            linkTo: marketsAllSpecials(currentEventType),
            onClick: onClick,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "d8ZJG6",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Browse participating brands"
                    }
                ]
            })
        })
    });
    return /*#__PURE__*/ _jsx(Wrapper, {
        children: /*#__PURE__*/ _jsxs(Container, {
            children: [
                /*#__PURE__*/ _jsxs(Content, {
                    children: [
                        /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                            desktopAndAbove: /*#__PURE__*/ _jsx(StyledMarketPrimaryLogo, {}),
                            tabletAndBelow: /*#__PURE__*/ _jsx(StyledFaireMarketIcon, {})
                        }),
                        /*#__PURE__*/ _jsxs(TopContent, {
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    children: [
                                        preambleIcon,
                                        preamble
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: "3x"
                                }),
                                title
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "3x"
                        }),
                        subtitle,
                        ctaButton
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "5x"
                }),
                /*#__PURE__*/ _jsx(HorizontalLine, {
                    color: getEventTheme("faireMarket").primaryLight
                })
            ]
        })
    });
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-dfbb85b7-0"
})([
    "background-color:",
    ";"
], getEventTheme("faireMarket").primaryDark);
var Container = styled.div.withConfig({
    componentId: "sc-dfbb85b7-1"
})([
    "max-width:1280px;padding:",
    ";margin:0 auto;",
    "{padding:",
    ";}"
], getSpacing("7x", "6x", 0), responsiveMediaQueries.mobile, getSpacing("5x", "2x", 0));
var Content = styled(Column).withConfig({
    componentId: "sc-dfbb85b7-2"
})([
    "position:relative;",
    "{padding-right:444px;}"
], responsiveMediaQueries.desktopAndAbove);
var TopContent = styled(Column).withConfig({
    componentId: "sc-dfbb85b7-3"
})([
    "",
    "{padding-right:",
    ";}"
], responsiveMediaQueries.tabletAndBelow, getSpacing("10x"));
var StyledButton = styled(Button).attrs({
    variant: "secondary"
}).withConfig({
    componentId: "sc-dfbb85b7-4"
})([
    "width:fit-content;border:0;",
    "{width:100%;}"
], responsiveMediaQueries.mobile);
var StyledMarketPrimaryLogo = styled(EventsThemeLogo.faireMarket.primary).attrs({
    isInverse: true,
    width: "220px",
    height: "177px"
}).withConfig({
    componentId: "sc-dfbb85b7-5"
})([
    "position:absolute;top:0;right:0;"
]);
var StyledFaireMarketIcon = styled(EventsThemeShape.faireMarket.badge).attrs({
    width: "64px"
}).withConfig({
    componentId: "sc-dfbb85b7-6"
})([
    "position:absolute;top:0;right:0;"
]);
var StyledInsider = styled(Insider).attrs({
    color: getEventTheme("faireMarket").promoTextInverse
}).withConfig({
    componentId: "sc-dfbb85b7-7"
})([
    "margin-top:2px;"
]);
